import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { Save, Smartphone, Wifi, ChevronDownIcon, ScanLine } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { Calendar } from '../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import BarcodeScannerDialog from '../../../components/BarcodeScannerDialog';

const labelClassName = 'text-sm font-medium text-foreground';
const fieldClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:border-primary/50';
const selectTriggerClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/25 focus:border-primary/50';

const DatePickerField = ({ id, label, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const parsedDate = value ? new Date(value) : undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className={labelClassName}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id={id}
            type="button"
            className={`w-full justify-between rounded-lg font-normal text-left ${fieldClassName}`}
          >
            {parsedDate
              ? parsedDate.toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
              : 'Select date'}
            <ChevronDownIcon className="h-4 w-4 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={parsedDate}
            captionLayout="dropdown-buttons"
            onSelect={(date) => {
              if (date) {
                onChange(format(date, 'yyyy-MM-dd'));
              } else {
                onChange('');
              }
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default function AddDevice() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [isLoadingDeviceTypes, setIsLoadingDeviceTypes] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const { post, get } = useApi();
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      device_type_id: '',
      imei_number: '',
      gps_card_no: '',
      gps_serial_no: '',
      sim_card: '',
      mobileNo: '',
      activate_date: '',
      device_status: 'In-Pool',
      notes: ''
    }
  });

  const fetchDeviceTypes = useCallback(async () => {
    setIsLoadingDeviceTypes(true);
    try {
      const response = await get('/device-types?perPage=1000');
      
      
      if (response?.success && Array.isArray(response?.data)) {
        setDeviceTypes(response.data);
      } else if (Array.isArray(response?.data)) {
        setDeviceTypes(response.data);
      } else if (Array.isArray(response)) {
        setDeviceTypes(response);
      } else {
        setDeviceTypes([]);
      }
    } catch (error) {
      toast.error('Failed to fetch device types');
      setDeviceTypes([]);
    } finally {
      setIsLoadingDeviceTypes(false);
    }
  }, [get]);

  // Fetch device types on component mount
  useEffect(() => {
    fetchDeviceTypes();
  }, [fetchDeviceTypes]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Validate device_type_id
      if (!data.device_type_id) {
        toast.error('Please select a model name');
        setIsLoading(false);
        return;
      }

      // Map form data to API expected format (camelCase or snake_case based on your API)
      const payload = {
        device_type_id: data.device_type_id,
        imei_number: data.imei_number,
        gps_card_no: data.gps_card_no,
        gps_serial_no: data.gps_serial_no,
        sim_card: data.sim_card,
        mobileNo: data.mobileNo,
        activate_date: data.activate_date ? `${data.activate_date} 10:30:00` : null,
        device_status: data.device_status,
        notes: data.notes
      };


      await post('/devices', payload);


      toast.success('Device created successfully!');
      navigate('/admin/devices');
    } catch (error) {
      
      let errorMessage = 'An error occurred while creating the device. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl bg-background text-foreground min-h-screen relative">
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Add New Device</h1>
          <p className="text-muted-foreground">Create a new tracking device for TrackYourWheelz</p>
        </div>
       
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        {/* Device Information */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Smartphone className="w-5 h-5" />
              Device Information
            </CardTitle>
            <CardDescription>
              Basic device details and identification information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 border-1 border-muted-foreground/20 rounded-lg p-4">
              <div className="space-y-2">
                <Label htmlFor="device_type_id" className={labelClassName}>Model Name *</Label>
                <Select 
                  onValueChange={(value) => setValue('device_type_id', value)} 
                  value={watch('device_type_id')}
                >
                  <SelectTrigger className={selectTriggerClassName} id="device_type_id">
                    <SelectValue placeholder={isLoadingDeviceTypes ? "Loading device types..." : "Select model name"} />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] border-1 border-muted-foreground/20">
                    {deviceTypes.length === 0 && !isLoadingDeviceTypes ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">No device types available</div>
                    ) : (
                      deviceTypes.map((deviceType) => {
                        const deviceTypeId = deviceType._id || deviceType.id;
                        const deviceName = deviceType.name || 'N/A';
                        const modelName = deviceType.modelName || deviceType.model_name || 'N/A';
                        return (
                          <SelectItem key={deviceTypeId} value={deviceTypeId}>
                            {deviceName} - {modelName}
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
                {errors.device_type_id && (
                  <p className="text-sm text-red-500">{errors.device_type_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="imei_number" className={labelClassName}>IMEI Number *</Label>
                <div className="flex gap-2">
                  <Input
                    id="imei_number"
                    type="tel"
                    {...register('imei_number', { 
                      required: 'IMEI number is required',
                      pattern: {
                        value: /^[0-9]{1,20}$/,
                        message: 'IMEI number must be between 1 and 20 digits and contain only numbers'
                      },
                      maxLength: {
                        value: 20,
                        message: 'IMEI number cannot exceed 20 digits'
                      }
                    })}
                    placeholder="Enter IMEI number (max 20 digits)"
                    className={fieldClassName}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 shrink-0 px-3"
                    onClick={() => setIsScannerOpen(true)}
                    title="Scan barcode or QR code"
                  >
                    <ScanLine className="h-4 w-4" />
                  </Button>
                </div>
                {errors.imei_number && (
                  <p className="text-sm text-red-500">{errors.imei_number.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="gps_card_no" className={labelClassName}>GPS Card Number *</Label>
                <Input
                  id="gps_card_no"
                  {...register('gps_card_no', { required: 'GPS card number is required' })}
                  placeholder="Enter GPS card number"
                  className={fieldClassName}
                />
                {errors.gps_card_no && (
                  <p className="text-sm text-red-500">{errors.gps_card_no.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="gps_serial_no" className={labelClassName}>GPS Serial Number *</Label>
                <Input
                  id="gps_serial_no"
                  {...register('gps_serial_no', { required: 'GPS serial number is required' })}
                  placeholder="Enter GPS serial number"
                  className={fieldClassName}
                />
                {errors.gps_serial_no && (
                  <p className="text-sm text-red-500">{errors.gps_serial_no.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sim_card" className={labelClassName}>SIM Card Number *</Label>
                <Input
                  id="sim_card"
                  {...register('sim_card', { 
                    required: 'SIM card number is required',
                    pattern: {
                      value: /^[0-9]{19,20}$/,
                      message: 'SIM card number must be 19-20 digits'
                    }
                  })}
                  placeholder="Enter SIM card number"
                  className={fieldClassName}
                />
                {errors.sim_card && (
                  <p className="text-sm text-red-500">{errors.sim_card.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobileNo" className={labelClassName}>MSISDN / Mobile Number *</Label>
                <Input
                  id="mobileNo"
                  type="tel"
                  {...register('mobileNo', {
                    required: 'Phone number is required',
                    pattern: {
                      value: /^[0-9]{10,15}$/,
                      message: 'Phone number must be 10 to 15 digits'
                    }
                  })}
                  placeholder="Enter phone number"
                  className={fieldClassName}
                />
                {errors.mobileNo && (
                  <p className="text-sm text-red-500">{errors.mobileNo.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="device_status" className={labelClassName}>Device Status *</Label>
                <Select onValueChange={(value) => setValue('device_status', value)} value={watch('device_status')} defaultValue="In-Pool">
                  <SelectTrigger className={selectTriggerClassName}>
                    <SelectValue placeholder="Select device status" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="In-Pool">In-Pool</SelectItem>
                    <SelectItem value="Maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
                {errors.device_status && (
                  <p className="text-sm text-red-500">{errors.device_status.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activation Information */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Wifi className="w-5 h-5" />
              Activation Information
            </CardTitle>
            <CardDescription>
              Device activation date and additional details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 border-1 border-muted-foreground/20 rounded-lg p-4">
              <div className="space-y-2">
                <DatePickerField
                  id="activate_date"
                  label="Activation Date"
                  value={watch('activate_date')}
                  onChange={(date) => setValue('activate_date', date)}
                />
                {errors.activate_date && (
                  <p className="text-sm text-red-500">{errors.activate_date.message}</p>
                )}
              </div>

              <div className="space-y-2 sm:col-span-1">
                {/* Placeholder for future fields */}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className={labelClassName}>Notes</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder="Enter any additional notes about the device (optional)"
                className={`min-h-[100px] rounded-lg ${fieldClassName}`}
                rows={4}
              />
              {errors.notes && (
                <p className="text-sm text-red-500">{errors.notes.message}</p> 
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-4">
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full px-6 text-sm font-semibold sm:h-12 sm:w-auto sm:min-w-[130px] border-1 border-white "
            onClick={() => navigate('/admin/devices')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}
          variant="outline"
          className="h-11 w-full px-6 text-sm font-semibold sm:h-12 sm:w-auto sm:min-w-[170px] border-1 border-muted-foreground/20"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4  mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2 border-1 border-muted-foreground/20" />
                Create Device
              </>
            )}
          </Button>
        </div>
      </form>

      <BarcodeScannerDialog
        open={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        title="Scan device IMEI"
        onScan={(value) => {
          const digits = String(value).replace(/\D/g, '').slice(0, 20);
          if (!digits) {
            toast.error('Scanned code did not contain a valid IMEI number.');
            return;
          }
          setValue('imei_number', digits, { shouldValidate: true, shouldDirty: true });
          toast.success('IMEI captured from scan.');
        }}
      />
    </div>
  );
}
